/**
 * ClienteHeader - Header component with cliente selector and actions
 */
import React from 'react';
import { Plus, Edit, Upload, Info } from 'lucide-react';
import type { Cliente } from '../../services/clienteService';
import { formatarCNPJ } from '../../lib/utils';
import PermissionGate from '../PermissionGate';

interface ClienteHeaderProps {
  clientes: Cliente[];
  filteredClientes: Cliente[];
  selectedCliente: Cliente | null;
  onSelectCliente: (cliente: Cliente) => void;
  onCreateNew: () => void;
  onEdit: () => void;
  onOpenImportModal: () => void;
  onOpenFormatInfo: () => void;
  produtosCount: number;
  representantes: { length: number };
  hospitais: { length: number };
  showInactive: boolean;
  onShowInactiveChange: (show: boolean) => void;
}

export const ClienteHeader: React.FC<ClienteHeaderProps> = ({
  clientes,
  filteredClientes,
  selectedCliente,
  onSelectCliente,
  onCreateNew,
  onEdit,
  onOpenImportModal,
  onOpenFormatInfo,
  produtosCount,
  representantes,
  hospitais,
  showInactive,
  onShowInactiveChange,
}) => {
  return (
    <>
      {/* Combo Box de Cliente com Botões ao Lado - Layout compacto */}
      <div className="mb-3">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-3 py-2">
          <div className="flex items-center gap-3">
            {/* Seletor de Cliente */}
            <div className="flex items-center gap-2 flex-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                Cliente:
              </label>
              <select
                value={selectedCliente?.id || ''}
                onChange={(e) => {
                  const cliente = clientes.find(c => c.id === Number(e.target.value));
                  if (cliente) onSelectCliente(cliente);
                }}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Selecione um cliente...</option>
                {filteredClientes.map(cliente => (
                  <option
                    key={cliente.id}
                    value={cliente.id}
                    className={!cliente.ativo ? 'text-gray-400 italic' : ''}
                  >
                    {cliente.nome_fantasia || cliente.razao_social}{!cliente.ativo && ' (Inativo)'}
                  </option>
                ))}
              </select>
              {/* Checkbox mostrar inativos */}
              <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => onShowInactiveChange(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4"
                />
                Mostrar inativos
              </label>
            </div>

            {/* Botões de Ação - Alinhados verticalmente */}
            <div className="flex items-center gap-2">
              <PermissionGate tela="CLIENTES_LIST" acao="criar">
                <button
                  onClick={onCreateNew}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white transition-all shadow-sm hover:shadow-md"
                  style={{ background: 'rgb(37, 99, 235)' }}
                >
                  <Plus className="w-4 h-4" />
                  Novo Cliente
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="criar">
                <button
                  onClick={onOpenImportModal}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-white transition-all shadow-sm hover:shadow-md"
                  style={{ background: 'rgb(22, 163, 74)' }}
                  title="Upload de Clientes via Excel"
                >
                  <Upload className="w-4 h-4" />
                  Upload
                </button>
              </PermissionGate>
              <PermissionGate tela="CLIENTES_LIST" acao="criar">
                <button
                  onClick={onOpenFormatInfo}
                  className="flex items-center justify-center w-8 h-8 text-sm font-medium border rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                  style={{
                    background: 'transparent',
                    color: 'rgb(107, 114, 128)',
                    borderColor: 'rgb(209, 213, 219)'
                  }}
                  title="Informações do formato Excel"
                >
                  <Info className="w-4 h-4" />
                </button>
              </PermissionGate>
              {selectedCliente && (
                <PermissionGate tela="CLIENTES_LIST" acao="editar">
                  <button
                    onClick={onEdit}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-md transition-colors hover:bg-blue-50"
                    style={{
                      background: 'transparent',
                      color: 'rgb(37, 99, 235)',
                      borderColor: 'rgb(37, 99, 235)'
                    }}
                  >
                    <Edit className="w-4 h-4" />
                    Editar
                  </button>
                </PermissionGate>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Client Header and Badges */}
      {selectedCliente && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {selectedCliente.nome_fantasia || selectedCliente.razao_social}
            </h2>
            <div className="flex gap-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                {produtosCount} Produtos
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-600 text-sm font-medium">
                {representantes.length} Representantes
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-100 text-purple-600 text-sm font-medium">
                {hospitais.length} Hospitais
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            CNPJ: {selectedCliente.cnpj ? formatarCNPJ(selectedCliente.cnpj) : '-'} | Código: {selectedCliente.codigo}
          </p>
        </div>
      )}
    </>
  );
};
